import { readableDateTimeFromDate } from '@utils/date-utils';
import { Box, Grid2 as Grid, Stack, Typography } from '@mui/material';
import MapPreview from '@atoms/MapPreview';
import useEventContext from '@hooks/useEventContext';
import SchedulingDetails from '@components/eventCreation/SchedulingDetails';
import EditEventGeneralInfo from '@components/eventCreation/EditEventGeneralInfo';
import SubmitGeneralEventInfoFAB from './SubmitGeneralEventInfoFAB';
import WeighInAtEntry from '@atoms/WeighInAtEntry';
import { getBirthdayCutOffOptions, WeighInItem } from '@type/db-types';
import CopyToClipboardButton from '@atoms/CopyToClipboardButton';
import ViewQRModal from '@components/molecules/ViewQRModal';

export default function EventGeneralInfo({ previewOnly = false }: { previewOnly?: boolean }) {
    const { event, isEventAdmin } = useEventContext();

    if (!event) return null;

    const {
        birthdayCutoff,
        description,
        fee,
        imageUrl,
        location,
        maxEntries,
        name,
        placeId,
        registrantsCount,
        startAt,
        weighInAt,
    } = event;

    const birthdayCutoffLabel = getBirthdayCutOffOptions(startAt).find(
        ({ value }) => value === birthdayCutoff,
    )?.label;

    return !previewOnly && isEventAdmin ? (
        <Grid container spacing={2}>
            <EditEventGeneralInfo />
            <SchedulingDetails />

            {event?.id ? (
                <Stack>
                    <Typography variant="h6" gutterBottom paddingTop={4}>
                        Share Event
                    </Typography>
                    <Stack flexDirection={'row'} justifyContent={'center'} marginBottom={4}>
                        <Box paddingRight={4}>
                            <CopyToClipboardButton
                                label="Copy Event Link"
                                text={`${window.location.origin}/events/${event?.id}`}
                            />
                        </Box>
                        <Box>
                            <ViewQRModal text={`${window.location.origin}/events/${event?.id}`} />
                        </Box>
                    </Stack>
                </Stack>
            ) : null}
            <SubmitGeneralEventInfoFAB />
        </Grid>
    ) : (
        <>
            <Grid container spacing={2}>
                <Grid size={{ xs: imageUrl ? 6 : 4 }}>
                    {imageUrl ? (
                        <>
                            <Typography
                                variant="h5"
                                sx={{
                                    display: { xs: 'block', lg: 'none' }, // show only on small screens
                                    padding: '1rem',
                                }}
                            >
                                {name}
                            </Typography>
                            <div
                                className="detail-element"
                                style={{
                                    backgroundImage: imageUrl ? `url(${imageUrl})` : 'none',
                                    backgroundSize: 'contain',
                                    backgroundRepeat: 'no-repeat',
                                    height: '300px', // match the height of the map preview
                                    display: 'flex',
                                }}
                            >
                                <Typography
                                    variant="h5"
                                    sx={{
                                        background: 'rgba(255,255,255,0.7)',
                                        // background:
                                        //     'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,.6) 30%, rgba(255,255,255,1) 100%)',
                                        display: { xs: 'none', lg: 'block' }, // hide on small screens
                                        padding: '1rem',
                                        width: '100%',
                                        alignSelf: 'flex-end',
                                    }}
                                >
                                    {name}
                                </Typography>
                            </div>
                        </>
                    ) : (
                        <div className="detail-element">
                            <Typography variant="subtitle1" className="label">
                                Event Name
                            </Typography>
                            <Typography variant="body1" className="value">
                                {name}
                            </Typography>
                        </div>
                    )}
                </Grid>

                {/* Move map preview if there is an Event image */}
                {imageUrl ? (
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <div className="detail-element">
                            <Typography variant="subtitle1" className="label">
                                Address
                            </Typography>
                            <Typography variant="body1" className="value">
                                {location}
                            </Typography>

                            {placeId?.length ? <MapPreview placeId={placeId} /> : null}
                        </div>
                    </Grid>
                ) : null}

                <Grid size={{ xs: 6, md: 8 }}>
                    <div className="detail-element">
                        <Typography variant="subtitle1" className="label">
                            Description
                        </Typography>
                        <Typography variant="body1" className="value">
                            {description}
                        </Typography>
                    </div>
                </Grid>

                <Grid size={{ xs: 6, md: 4 }}>
                    <div className="detail-element">
                        <Typography variant="subtitle1" className="label">
                            Wrestling Start Time
                        </Typography>
                        <Typography variant="body1" className="value">
                            {startAt ? readableDateTimeFromDate(startAt) : 'TBD'}
                        </Typography>
                    </div>
                </Grid>
                <Grid size={{ xs: 6, md: 4 }}>
                    <div className="detail-element">
                        <Typography variant="subtitle1" className="label">
                            Wrestler age as of
                        </Typography>
                        <Typography variant="body1" className="value">
                            {birthdayCutoffLabel || 'TBD'}
                        </Typography>
                    </div>
                </Grid>
                {/* show currently registered count */}
                <Grid size={{ xs: 6, md: 4 }}>
                    <div className="detail-element">
                        <Typography variant="subtitle1" className="label">
                            Registered Wrestlers
                        </Typography>
                        <Typography variant="body1" className="value">
                            {registrantsCount}
                            {maxEntries ? ` / ${maxEntries}` : ''}
                        </Typography>
                    </div>
                </Grid>
                <Grid size={{ xs: 6, md: 4 }}>
                    <div className="detail-element">
                        <Typography variant="subtitle1" className="label">
                            Registration Fee
                        </Typography>
                        <Typography variant="body1" className="value">
                            {fee ? `$${fee}` : 'Free'}
                        </Typography>
                    </div>
                </Grid>

                {/* Move map preview if there is an Event image */}
                {imageUrl ? null : (
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <div className="detail-element">
                            <Typography variant="subtitle1" className="label">
                                Address
                            </Typography>
                            <Typography variant="body1" className="value">
                                {location}
                            </Typography>

                            {placeId?.length ? <MapPreview placeId={placeId} /> : null}
                        </div>
                    </Grid>
                )}

                <Grid size={{ xs: 6 }}>
                    <div className="detail-element">
                        {((weighInAt as WeighInItem[]) || null)?.map((item, index) => (
                            <WeighInAtEntry key={index} item={item} index={index} />
                        ))}
                    </div>
                </Grid>
            </Grid>
        </>
    );
}
